import React, { useEffect, useRef } from "react";
import { FaPlane } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Get_All_Blogs_Action } from "../Redux/Actions/BlogsAction";
import { Blogs } from "../component/Blogs";
import { ClientOpinion } from "../component/ClientOpinion";
import { ContactUs } from "../component/ContactUs";
import FlightSightseeingHome from "../component/FlightSightseeingHome";
import { HeroSection } from "../component/Herosection";
import { useLanguage } from "../component/LanguageProvider";
import { OurServices } from "../component/OurServices";
import SEO from "../component/SEO";

const items = [
  {
    image:
      "https://images.pexels.com/photos/1388030/pexels-photo-1388030.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/ourservicepage",
    title: "Golden Visa",
    description:
      "Invest in your future with Spain's residency-by-investment program.",
  },
  {
    image:
      "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/ourservicepage",
    title: "Digital Nomad",
    description:
      "Work remotely from the sunny coasts of Spain with professional legal support.",
  },
  {
    image:
      "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/ourservicepage",
    title: "Company Setup",
    description:
      "Launch your business in the European market with ease and efficiency.",
  },
  {
    image:
      "https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/ourservicepage",
    title: "Legal Support",
    description:
      "Expert legal and tax advice to ensure your compliance in Spain.",
  },
  {
    image:
      "https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/ourservicepage",
    title: "Relocation",
    description:
      "Complete settlement services for you and your family in Europe.",
  },
];

/* Ticker strip content */
const TICKER_ITEMS_EN = [
  "Spain Residency",
  "Private Jet Charter",
  "Elite Relocation",
  "Golden Visa",
  "Company Formation",
  "VIP Concierge",
  "Aerial Advertising",
  "Flight Sightseeing",
  "Luxury Real Estate",
];
const TICKER_ITEMS_AR = [
  "الإقامة في إسبانيا",
  "طيران خاص",
  "انتقال فاخر",
  "فيزا ذهبية",
  "تأسيس شركات",
  "كونسيرج VIP",
  "إعلانات جوية",
  "جولات طيران",
  "عقارات فاخرة",
];

/* Animated stats strip */
const STATS_EN = [
  { value: "500+", label: "Relocations" },
  { value: "98%", label: "Success Rate" },
  { value: "11", label: "Premium Services" },
  { value: "2024", label: "Established" },
];
const STATS_AR = [
  { value: "500+", label: "عملية انتقال" },
  { value: "98%", label: "نسبة النجاح" },
  { value: "11", label: "خدمة متميزة" },
  { value: "2024", label: "تأسست" },
];

/* Counter hook */
function useCountUp(target, duration = 1800, trigger) {
  const [display, setDisplay] = React.useState("0");
  React.useEffect(() => {
    if (!trigger) return;
    const isNum = /^\d+$/.test(target);
    if (!isNum) {
      setDisplay(target);
      return;
    }
    const end = parseInt(target, 10);
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplay(String(Math.round(ease * end)));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [trigger, target]);
  return display;
}

function StatItem({ value, label }) {
  const ref = useRef(null);
  const [visible, setVisible] = React.useState(false);
  const suffix = value.replace(/\d/g, "");
  const numPart = value.replace(/\D/g, "");
  const counted = useCountUp(numPart, 1800, visible);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="fv-stat-item">
      <span className="fv-stat-counter">
        {numPart ? counted + suffix : value}
      </span>
      <span className="fv-stat-label">{label}</span>
    </div>
  );
}

function TickerStrip({ items }) {
  const doubled = [...items, ...items];
  return (
    <div className="fv-ticker-strip" style={{ overflow: "hidden" }}>
      <div className="fv-ticker-track">
        {doubled.map((item, i) => (
          <div key={i} className="fv-ticker-item">
            <span>✦</span>
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

export const HomePage = () => {
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "FLYVIA",
    url: "https://flyvia.org/",
    description:
      "FLYVIA - Your Trusted Partner in your business and travel to Europe.",
    publisher: {
      "@type": "Organization",
      name: "FLYVIA",
      url: "https://flyvia.org/",
      logo: "https://flyvia.org/Flyvia%20Logo.png",
    },
  };

  const { currentLang, updateKey } = useLanguage();
  const isAR = currentLang === "ar";
  const tickerItems = isAR ? TICKER_ITEMS_AR : TICKER_ITEMS_EN;
  const stats = isAR ? STATS_AR : STATS_EN;

  /* Scroll-reveal: attach IntersectionObserver to all [data-reveal] elements */
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });

  /* Animated dividers */
  useEffect(() => {
    const dividers = document.querySelectorAll(".fv-animated-divider");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("revealed");
        });
      },
      { threshold: 0.5 },
    );
    dividers.forEach((d) => obs.observe(d));
    return () => obs.disconnect();
  });

  const dispatch = useDispatch();
  const getallblogs = async () => {
    await dispatch(Get_All_Blogs_Action(`limit=3&sort=-createdAt`));
  };

  useEffect(() => {
    getallblogs();
  }, [dispatch]);

  const resblogs = useSelector((state) => state.AllBlogs.Blog);

  console.log(resblogs, "resblogs");
  return (
    <div>
      <SEO
        title={currentLang === "ar" ? "الرئيسية" : "Home"}
        description={
          currentLang === "ar"
            ? "FlyVia - شريكك الموثوق في أعمالك وسفرك إلى أوروبا. نحن نقدم خدمات احترافية للإقامة وتأسيس الشركات."
            : "FlyVia - Your Trusted Partner in your business and travel to Europe. We provide professional services for residency and company formation."
        }
        keywords={
          currentLang === "ar"
            ? "سفر, أعمال, أوروبا, FlyVia, استشارة, خدمات, إقامة إسبانيا, فيزا"
            : "travel, business, Europe, FLYVIA, consultation, services, Spain residency, Golden Visa"
        }
        schemaMarkup={homeSchema}
      />

      <HeroSection key={`hero-${currentLang}-${updateKey}`} />

      {/* Animated ticker strip after hero */}
      <TickerStrip items={tickerItems} />

      {/* Animated stats strip */}
      <div
        style={{
          background: "var(--premium-bg)",
          borderBottom: "1px solid rgba(174,0,1,0.15)",
          position: "relative",
          zIndex: 10,
          paddingBottom: "20px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            maxWidth: "1000px",
            margin: "0 auto",
            padding: "0 24px",
            width: "100%",
          }}
        >
          {stats.map((s, i) => (
            <StatItem key={i} value={s.value} label={s.label} />
          ))}
        </div>
      </div>

      <OurServices key={`services-${currentLang}-${updateKey}`} />
      {/* <div 
      className="mx-auto"
      style={{ height: '800px', position: 'relative' }}>
        <InfiniteMenu items={items}
          scale={10}
        />
      </div> */}
      {/* <div
      className='mx-auto premium-bg-texture !overflow-hidden'
      style={{ width: '100%', height: '100vh' }}>
        <DomeGallery
          fit={0.2}
          minRadius={600}
          maxVerticalRotationDeg={0}
          segments={24}
          dragDampening={2}
          grayscale
        />
      </div> */}

      <FlightSightseeingHome key={`sightseeing-${currentLang}-${updateKey}`} />

      {/* Ticker between sections */}
      <TickerStrip items={[...tickerItems].reverse()} />

      <ClientOpinion key={`opinion-${currentLang}-${updateKey}`} />

      {/* Animated plane divider */}
      <div
        style={{ background: "var(--premium-bg)", padding: "40px 0" }}
        data-reveal="fade"
      >
        <div
          className="fv-plane-divider mx-auto"
          style={{ color: "rgba(174,0,1,0.7)", fontSize: "22px" }}
        >
          <FaPlane
            style={{ filter: "drop-shadow(0 0 8px rgba(174,0,1,0.6))" }}
          />
        </div>
        <div
          className="fv-animated-divider mx-auto mt-3"
          style={{ width: "60%", maxWidth: "500px" }}
        />
      </div>

      <Blogs
        key={`blogs-${currentLang}-${updateKey}`}
        threeblogs={resblogs?.data?.latestBlogs || resblogs?.data?.blogs}
        bgColor="var(--premium-bg)"
        mainColor="var(--primary-color)"
      />
      <ContactUs
        key={`contact-${currentLang}-${updateKey}`}
        bgColor="var(--premium-bg)"
        mainColor="var(--primary-color)"
      />
      {/* <FloatingContact key={`floating-${currentLang}-${updateKey}`} /> */}
      {/* <Footer key={`footer-${currentLang}-${updateKey}`} /> */}
    </div>
  );
};
